import React, { useState, useEffect, useRef, useMemo } from 'react';

interface LazyImageProps {
  src: string;
  alt: string;
  className?: string;
  placeholderSrc?: string;
  width?: number | string;
  height?: number | string;
  onLoad?: () => void;
  priority?: boolean;
  quality?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
}

// Preload cache for priority images
const preloadedImages = new Map<string, string>();

const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt,
  className = '',
  placeholderSrc = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9IiNlOWVjZWYiLz48L3N2Zz4=',
  width,
  height,
  onLoad,
  priority = false,
  quality = 75,
  objectFit = 'cover',
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [imageSrc, setImageSrc] = useState(preloadedImages.get(src) || placeholderSrc);
  const imgRef = useRef<HTMLImageElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Optimize image URL if it's from Cloudinary
  const optimizedSrc = useMemo(() => {
    if (!src) return placeholderSrc;
    
    // If it's a Cloudinary URL, add optimization parameters
    if (src.includes('cloudinary.com')) {
      // Check if URL already has parameters
      const hasParams = src.includes('?');
      const separator = hasParams ? '&' : '?';
      
      // Add quality and auto format parameters
      return `${src}${separator}q=${quality}&auto=format,compress`;
    }
    
    return src;
  }, [src, quality, placeholderSrc]);

  // Preload priority images immediately
  useEffect(() => {
    if (priority && !preloadedImages.has(src)) {
      const img = new Image();
      img.src = optimizedSrc;
      img.onload = () => {
        preloadedImages.set(src, optimizedSrc);
        setImageSrc(optimizedSrc);
        setIsLoaded(true);
        if (onLoad) onLoad();
      };
      img.onerror = () => {
        setHasError(true);
      };
    }
  }, [priority, optimizedSrc, src, onLoad]);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(preloadedImages.has(src));
    setHasError(false);
    setImageSrc(preloadedImages.get(src) || placeholderSrc);
    
    // If priority is true or image is already preloaded, load immediately
    if (priority || preloadedImages.has(src)) {
      if (!isLoaded) {
        loadImage();
      }
      return;
    }
    
    if (imgRef.current) {
      // Disconnect previous observer if it exists
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      // Create new IntersectionObserver
      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting) {
          loadImage();
          
          // Disconnect observer after loading
          if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
          }
        }
      }, {
        rootMargin: '200px 0px', // Start loading when image is 200px from viewport
        threshold: 0.01
      });

      // Start observing the image element
      observerRef.current.observe(imgRef.current);
    }

    // Cleanup function
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [optimizedSrc, placeholderSrc, onLoad, priority, src, isLoaded]);

  const loadImage = () => {
    // If image is already preloaded, use it
    if (preloadedImages.has(src)) {
      setImageSrc(preloadedImages.get(src)!);
      setIsLoaded(true);
      if (onLoad) onLoad();
      return;
    }

    const img = new Image();
    img.src = optimizedSrc;
    
    img.onload = () => {
      preloadedImages.set(src, optimizedSrc);
      setImageSrc(optimizedSrc);
      setIsLoaded(true);
      if (onLoad) onLoad();
    };
    
    img.onerror = () => {
      console.error(`Failed to load image: ${optimizedSrc}`);
      setHasError(true);
      // Try to use a fallback image for products
      if (src.includes('product') || alt.toLowerCase().includes('makhana')) {
        setImageSrc('/placeholder-product.jpg');
      }
    };
  };

  return (
    <div 
      className={`relative overflow-hidden ${className}`} 
      style={{ width, height }}
    >
      <img
        ref={imgRef}
        src={imageSrc}
        alt={alt}
        className={`w-full h-full transition-opacity duration-150 ${isLoaded ? 'opacity-100' : 'opacity-60'}`}
        style={{ objectFit }}
        width={width}
        height={height}
        loading={priority ? 'eager' : 'lazy'}
        onError={() => setHasError(true)}
      />
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-20">
          <div className="animate-pulse rounded-full h-8 w-8 border-2 border-green-500 border-t-transparent animate-spin"></div>
        </div>
      )}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-cream-100 to-cream-200">
          <div className="bg-brand-100 rounded-full p-3 mb-2">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <p className="text-xs text-brand-600 font-medium">Product Image</p>
        </div>
      )}
    </div>
  );
};

export default LazyImage;
